import * as React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import {
	GdsService_OrderAdditionalServiceGdsServiceMisc_Fragment as MiscServiceFragment,
	TravellerFieldEnum
} from '@websky/graphql';
import { Props as CardExtraContentProps } from '../CardExtraContent/CardExtraContent';
import { useTheme } from '../../../theme';
import { useConfig } from '../../../context';
import { getFfpBonusesForFlightByTravellers, getMilesForOrder } from '../../../Checkout/store/order/selectors';
import { getServiceSummaryType } from '../../utils';
import { useCardExtraCtx } from '../CardExtra.context';
import { ServiceSummary } from '../../../features/service-summary';
import Button from '../../../BaseComponents/Button';
import CartItemOptionAdapter from '../../../Cart/CartItemOptionAdapter/CartItemOptionAdapter';
import ServiceSummaryDivider from '../../../ServiceSummary/components/ServiceSummaryDivider/ServiceSummaryDivider';
import { CdnIcon } from '@shared/ui';
import { useCardExtraSummaryDelete } from './hooks';
import { getTravellerName, getUserValue } from '../../../utils';
import LoyaltySummary from '../../../ServiceSummary/services/LoyaltySummary';
import { ServiceOptionItem } from 'ServiceSummary/types';

type CardExtraContentPickProps = Pick<
	CardExtraContentProps,
	| 'icon'
	| 'content'
	| 'title'
	| 'description'
	| 'style'
	| 'isSelected'
	| 'additionalControls'
	| 'hideFooterControls'
	| 'additionalText'
	| 'aggregationIcons'
	| 'coverClassName'
>;

interface Props {
	children: (props: CardExtraContentPickProps) => React.ReactElement;
}

const CardExtraSummary: React.FC<Props> = ({ children }) => {
	const { t } = useTranslation('CardExtra');
	const { CardExtraContent: theme } = useTheme('CardExtra');
	const { Loyalty: loyaltyTheme } = useTheme('CheckoutAdditionalService');

	const {
		global: {
			companyInfo: { loyaltyProgramName }
		}
	} = useConfig();

	const milesBonus = useSelector(getMilesForOrder);

	const {
		type,
		filter,
		flight,
		travellers,
		additionalServicesByType,
		userInfo,
		isLoading,
		isCheckinAvailable,
		variant,
		segmentId
	} = useCardExtraCtx();

	const onDelete = useCardExtraSummaryDelete();

	if (type === 'loyalty') {
		// TODO: вынести из CardExtra, лучше передавать снаружи
		const ffpBonusesForFlightByTravellers = useSelector(getFfpBonusesForFlightByTravellers);

		const travellersWithAvailableLoyalty = travellers.filter(traveller =>
			traveller.values.some(value => value.type === TravellerFieldEnum.LoyaltyNumber)
		);

		const options = travellersWithAvailableLoyalty.map(traveller => {
			const card = getUserValue(traveller, TravellerFieldEnum.LoyaltyNumber);
			const points = ffpBonusesForFlightByTravellers?.[traveller.id]?.bonusForFlight;
			const items: ServiceOptionItem[] = [
				{
					id: traveller.id,
					name: card || t('EmptyCard'),
					included: !!card
				}
			];

			if (points) {
				items.push({
					id: traveller.id,
					name: t('PointsWillBeCredited'),
					included: false,
					description: String(points)
				});
			}

			return {
				header: getTravellerName(traveller),
				items
			};
		});

		return children({
			content: (
				<>
					{milesBonus > 0 ? (
						<>
							<span>
								{t('MilesAwarded', {
									number: milesBonus,
									miles: t('Miles', { count: milesBonus })
								})}
							</span>
							<span>{t('MilesWillBeCredited')}</span>
						</>
					) : (
						<span>{t('MilesNotCredited')}</span>
					)}
					{!userInfo?.userFfpInfo && options.length === 0 && <span>{t('StartSavingNow')}</span>}
					<LoyaltySummary options={options} />
				</>
			),
			title: loyaltyProgramName,
			isSelected: !!userInfo?.userFfpInfo || options.some(option => option.items.some(item => item.included)),
			aggregationIcons: ['Info/Subscribe', 'Services/Miles'],
			coverClassName: cn({ [loyaltyTheme.loyalty_aggregator]: variant === 'aggregator' })
		});
	}

	return (
		<ServiceSummary
			serviceType={getServiceSummaryType(type)}
			filter={filter}
			onDelete={onDelete}
			segmentId={segmentId}
			renderSummary={groups => {
				let icon: string;
				let title: string;
				let description: string;
				let content: React.ReactNode;
				let additionalControls: React.ReactNode;
				let hideFooterControls = false;
				let isSelected = false;

				const style: CardExtraContentProps['style'] = {};

				if (type === 'insurance' && additionalServicesByType.insurances?.insurancePrograms?.length === 1) {
					title = additionalServicesByType.insurances?.insurancePrograms?.[0]?.name;
				}

				if (groups.length) {
					content = (
						<ServiceSummaryDivider>
							{groups.map(group => {
								if (!description && group.items[0]?.description) {
									description = group.items[0].description;
								}

								return (
									<CartItemOptionAdapter option={group} key={group.header + group.direction?.id} />
								);
							})}
						</ServiceSummaryDivider>
					);
					isSelected = true;
				}

				switch (type) {
					case 'seats':
						const isSeatsAvailableInCheckinOnly = flight.seatMap.segments?.every(
							segment => segment.isSeatsAvailableInCheckinOnly
						);

						if (isSeatsAvailableInCheckinOnly) {
							description = isCheckinAvailable
								? t('Seat selection is available in the online registration service')
								: t('You can select seats using the online registration service after issuing tickets');

							hideFooterControls = true;
						}
						break;
					case 'insurance':
						if (!isSelected) {
							description = additionalServicesByType.insurances?.insurancePrograms?.[0]?.description;
						}
						break;
					case 'transfer':
						if (isSelected && !isLoading) {
							additionalControls = (
								<Button
									variant="optionEdit"
									onClick={() => window.open('https://aeroexpress.ru', '_blank')}
								>
									{t('Aeroexpress:View schedule')}
								</Button>
							);
						}
						break;
					case 'business-lounge':
					case 'priority-boarding':
					case 'baggage-delivery':
					case 'capsule-hotel':
					case 'sms':
						if (!isSelected) {
							description = additionalServicesByType.gdsServices?.services?.[0]?.description;
						}
						break;

					case 'upgrade':
						const service = additionalServicesByType.gdsServices?.services?.[0];

						title = service?.name;
						hideFooterControls = isSelected;

						if (!isSelected) {
							description = service?.description;
						}

						break;
					case 'refundable-ticket': {
						hideFooterControls = true;

						content = (
							<>
								<div className={theme.refundableTicket__content}>
									<div className={theme.refundableTicket__description}>
										{t('ServiceDescription.refundable-ticket')}
									</div>

									<div className={theme.refundableTicket__message}>
										<CdnIcon icon="Info/Warning" />

										<span className={theme.refundableTicket__text}>
											{t(
												'You cannot remove this service from your booking at this stage of the booking process. The refund service price is added to the tariff and fees'
											)}
										</span>
									</div>
								</div>
							</>
						);

						break;
					}
					case 'misc': {
						const miscService = additionalServicesByType.gdsServices?.services?.[0] as MiscServiceFragment;

						if (miscService) {
							icon = miscService.icon;
							title = miscService.name;
							description = miscService.description;
							style.displayOrder = miscService.displayOrder;
							style.headerBg = miscService.backgroundColor;
						}
					}
				}

				return children({
					icon,
					title,
					description,
					content,
					style,
					additionalControls,
					hideFooterControls,
					isSelected
				});
			}}
		/>
	);
};

export default CardExtraSummary;
